import { useQuery } from 'react-query';
import { useApi } from 'utils/api';
import { AVAILABILITY_BASE_PATH } from 'constants/sharedConstants';
import { useClinicianInfo } from 'features/useClinicianInfo';
import { useSelector } from 'react-redux';
import { Store } from 'types/store';
import { AxiosError } from 'axios';
import { GuestAppointmentResponse } from '../types';
import { GUEST_APPOINTMENT_TAG } from '../constants';
import { getGuestAppointmentIdFromSession, getQueryKey } from '../utils';

export const useGuestAppointment = (disabled = false, includeDeleted = false) => {
  const api = useApi();
  const guestAppointmentId = getGuestAppointmentIdFromSession();
  const patientId = useSelector((store: Store) => store.global_reducer.current_user.attributes?.patient?.id);
  const queryKey = getQueryKey({ disabled, patientId, includeDeleted });

  const guestAppointmentQuery = useQuery(
    queryKey,
    () =>
      api
        .get<GuestAppointmentResponse>(
          guestAppointmentId
            ? `/guest-appointments/${guestAppointmentId}`
            : `/guest-appointments/patients/${patientId}/latest`,
          {
            baseURL: AVAILABILITY_BASE_PATH,
            params: {
              includeDeleted
            }
          }
        )
        .then(apiResponse => apiResponse.data),
    {
      enabled: !!api && (!!guestAppointmentId || !!patientId) && !disabled,
      retryDelay: 100,
      retry: (failureCount, error) => {
        const axiosError = error as AxiosError;
        const isNotFound = axiosError?.response?.status === 404;
        // Do not retry if the appointment is not found or if we have already retried twice
        return !(isNotFound || failureCount >= 2);
      },
      meta: {
        errorMessage: 'Failed to get guest appointments',
        tags: [GUEST_APPOINTMENT_TAG, guestAppointmentId ? 'byId' : 'byPatientId']
      }
    }
  );

  const clincianInfoQuery = useClinicianInfo(guestAppointmentQuery.data?.clinicianUserId);

  const isLoading = guestAppointmentQuery.isLoading || clincianInfoQuery.isLoading;
  const isError = guestAppointmentQuery.isError || clincianInfoQuery.isError;
  const isSuccess = guestAppointmentQuery.isSuccess && clincianInfoQuery.isSuccess;
  const isIdle = guestAppointmentQuery.isIdle || clincianInfoQuery.isIdle;

  const refetch = async () => {
    const { data: guestData } = await guestAppointmentQuery.refetch();

    const clinicianInfo = guestData?.clinicianUserId ? (await clincianInfoQuery.refetch()).data : undefined;

    return {
      ...guestData,
      clinicianInfo
    };
  };
  const data = isSuccess
    ? {
        ...guestAppointmentQuery.data,
        clinicianInfo: clincianInfoQuery.data
      }
    : undefined;

  return {
    data,
    isLoading,
    isError,
    isSuccess,
    refetch,
    isIdle
  };
};
